import { defineComponent as _defineComponent } from 'vue'
import { computed, watch, onMounted, onBeforeMount } from 'vue';
import { useRouter, useRoute } from 'vue-router';
import { CONFIG } from '@/const/config';

import { logout } from '@/helpers/utils';
import { setGlobalVueInstance } from '@/helpers/vueInstance';
import { useTheme } from '@modules/app/composables/useTheme';
import { useSessionTimeout } from '@modules/app/composables/useSessionTimeout';
import { initPullToRefresh } from '@modules/app/composables/usePullToRefresh';
import { useLegalLinksStore } from '@modules/shared/stores/legalLinks';
import SessionTimeoutModal from '@modules/app/components/SessionTimeoutModal.vue';
import PullToRefreshIndicator from '@modules/app/components/PullToRefreshIndicator.vue';
import ToastContainer from '@/components/ui/Toast/ToastContainer.vue';

// Import layout components
import AuthLayout from '@modules/app/layouts/AuthLayout.vue';
import FloatingSlimLayout from '@modules/app/layouts/FloatingSlimLayout.vue';

// Composables

export default /*@__PURE__*/_defineComponent({
  __name: 'App',
  setup(__props, { expose: __expose }) {
  __expose();

const router = useRouter();
const route = useRoute();
const { isDark } = useTheme();
const { start: startSessionTimeout, stop: stopSessionTimeout } = useSessionTimeout();

// Computed
const layout = computed(() => {
  const layoutName = route.meta['layout'] || 'auth';
  return `${layoutName}-layout`;
});

// Auth-flow pages (login, registration, forgot-password, verification) have
// hardcoded light backgrounds. If the user enabled dark mode while logged in
// and then logs out, the persisted `.dark` class on <html> turns
// `var(--theme-text-primary)` near-white on a still-light background — making
// labels like "I agree to the" invisible. Force light on those layouts and
// restore the user's preference on the rest.
const FORCE_LIGHT_LAYOUTS = ['empty', 'auth'];
watch(
  () => route.meta['layout'],
  (layoutName) => {
    const force = typeof layoutName === 'string' && FORCE_LIGHT_LAYOUTS.includes(layoutName);
    const root = document.documentElement;
    if (force) {
      root.classList.remove('dark');
    } else if (isDark.value) {
      root.classList.add('dark');
    }
  },
  { immediate: true }
);

// Layout component mapping
const layoutComponents: Record<string, any> = {
  'auth-layout': AuthLayout,
  'floating-slim-layout': FloatingSlimLayout,
  'empty-layout': 'div',
};

// Methods
const update = async (): Promise<void> => {
  // Handle service worker updates
  if ((window as any).$workbox) {
    await (window as any).$workbox.messageSW({ type: 'SKIP_WAITING' });
  }
};

const exit = async (): Promise<void> => {
  await logout();
  await router.push({ name: 'new-login' });
};

// Session timeout: start/stop based on route
watch(
  () => route.path,
  (path) => {
    startSessionTimeout(path);
  },
  { immediate: true }
);

// Lifecycle hooks
onBeforeMount(() => {
  // Set global Vue instance for helper functions
  setGlobalVueInstance();
  
  if ((window as any).$workbox) {
    (window as any).$workbox.addEventListener('waiting', () => {
      console.log('PLEASE UPDATE FOR NEW VERSION');
      update();
    });
  }
});

onMounted(() => {
  console.log({
    CONFIG,
    'process.env': process.env,
  });

  initPullToRefresh();

  // Prime Privacy Policy / Terms URLs so consumers (auth forms, settings, etc.)
  // can render external links without each one re-fetching.
  useLegalLinksStore().loadLegalLinks();
});

const __returned__ = { router, route, isDark, startSessionTimeout, stopSessionTimeout, layout, FORCE_LIGHT_LAYOUTS, layoutComponents, update, exit, SessionTimeoutModal, PullToRefreshIndicator, ToastContainer }
Object.defineProperty(__returned__, '__isScriptSetup', { enumerable: false, value: true })
return __returned__
}

})